import { useState, useEffect } from "react";
import api from "../../utils/api";
import { INP, S, THEME, TblRow, TblCell, Pill, tblHead, rolePill, TBL_SELECT, useIsMobile } from "./AdminComponents";
import { useLanguage } from "../../context/LanguageContext";

const { G, G3, O, O2 } = THEME;

const ROLE_LABELS = {
  ne: {
    admin:       "एडमिन",
    editor:      "सम्पादक",
    reporter:    "रिपोर्टर",
    volunteer:   "स्वयंसेवक",
    contributor: "योगदानकर्ता",
    public:      "सामान्य",
  },
  en: {
    admin:       "Admin",
    editor:      "Editor",
    reporter:    "Reporter",
    volunteer:   "Volunteer",
    contributor: "Contributor",
    public:      "Public",
  },
};

const ASSIGNABLE_ROLES = ["public", "reporter", "editor", "admin"];

const CONTENT = {
  ne: {
    statTotal:         "कुल",
    statEditor:        "सम्पादक",
    statReporter:      "रिपोर्टर",
    statAdmin:         "एडमिन",
    deltaSoFar:        "अहिलेसम्म",
    deltaEditor:       "सम्पादक",
    deltaReporter:     "रिपोर्टर",
    deltaAdmin:        "प्रशासक",
    searchPlaceholder: "🔍 नाम वा इमेलद्वारा खोज्नुहोस्…",
    filterAllRoles:    "सबै भूमिका",
    refresh:           "↻ ताजा गर्नुहोस्",
    colName:           "नाम",
    colEmail:          "इमेल",
    colCurrentRole:    "हालको भूमिका",
    colJoined:         "दर्ता मिति",
    colAssignRole:     "भूमिका तोक्नुहोस्",
    loading:           "प्रयोगकर्ताहरू लोड हुँदैछ…",
    empty:             "कुनै प्रयोगकर्ता भेटिएन।",
    errLoad:           "प्रयोगकर्ताहरू लोड गर्न असफल भयो।",
    errRole:           "भूमिका अपडेट गर्न असफल भयो।",
    locale:            "ne-NP",
    colActions:  "कार्य",
    deleteBtn:   "🗑",
    confirmDel:  "के तपाईं यो प्रयोगकर्तालाई मेटाउन चाहनुहुन्छ?",
    errDelete:   "प्रयोगकर्ता मेटाउन असफल भयो।",
  },
  en: {
    statTotal:         "Total",
    statEditor:        "Editors",
    statReporter:      "Reporters",
    statAdmin:         "Admins",
    deltaSoFar:        "So far",
    deltaEditor:       "Editors",
    deltaReporter:     "Reporters",
    deltaAdmin:        "Admins",
    searchPlaceholder: "🔍 Search by name or email…",
    filterAllRoles:    "All Roles",
    refresh:           "↻ Refresh",
    colName:           "Name",
    colEmail:          "Email",
    colCurrentRole:    "Current Role",
    colJoined:         "Joined",
    colAssignRole:     "Assign Role",
    loading:           "Loading users…",
    empty:             "No users found.",
    errLoad:           "Failed to load users.",
    errRole:           "Failed to update role.",
    locale:            "en-US",
    colActions:  "Actions",
    deleteBtn:   "🗑",
    confirmDel:  "Are you sure you want to delete this user?",
    errDelete:   "Failed to delete user.",
  },
};

function UsersPage() {
  const { language } = useLanguage();
  const t = CONTENT[language] || CONTENT.en;
  const roleLabels = ROLE_LABELS[language] || ROLE_LABELS.en;
  const isMobile = useIsMobile();

  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [search, setSearch]         = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [saving, setSaving]         = useState(null);

  useEffect(() => { fetchUsers(); }, []);

  async function fetchUsers() {
    setLoading(true); setError("");
    try {
      const res  = await api.get("/users");
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setUsers(data);
    } catch (e) {
      setError(e.response?.data?.message || t.errLoad);
    } finally {
      setLoading(false);
    }
  }

  async function updateRole(id, role) {
    setSaving(id);
    try {
      const res     = await api.patch(`/users/${id}/role`, { role });
      const updated = res.data?.data ?? res.data;
      setUsers(u => u.map(x => x._id === id ? updated : x));
    } catch (e) {
      alert(e.response?.data?.message || t.errRole);
    } finally {
      setSaving(null);
    }
  }
  async function deleteUserHandler(id) {
    if (!window.confirm(t.confirmDel)) return;
    setSaving(id);
    try {
      await api.delete(`/users/${id}`);
      setUsers(u => u.filter(x => x._id !== id));
    } catch (e) {
      alert(e.response?.data?.message || t.errDelete);
    } finally {
      setSaving(null);
    }
  }

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    const matchRole   = filterRole === "all" || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const stats = [
    { label: t.statTotal,    value: users.length,                                    icon: "👥", delta: t.deltaSoFar   },
    { label: t.statEditor,   value: users.filter(u => u.role === "editor").length,   icon: "✏️", delta: t.deltaEditor  },
    { label: t.statReporter, value: users.filter(u => u.role === "reporter").length, icon: "📰", delta: t.deltaReporter },
    { label: t.statAdmin,    value: users.filter(u => u.role === "admin").length,    icon: "🛡", delta: t.deltaAdmin   },
  ];

  function fmt(dateStr) {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString(t.locale, { day: "numeric", month: "short", year: "numeric" });
  }

  const USER_COLS = "minmax(100px,1fr) minmax(140px,1.6fr) max-content max-content 128px 60px";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Stat cards: 4 on desktop, 2 on mobile */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 10 }}>
        {stats.map(s => (
          <div key={s.label} style={S.statCard}>
            <div style={S.statIcon}>{s.icon}</div>
            <div>
              <div style={S.statVal}>{s.value}</div>
              <div style={S.statLabel}>{s.label}</div>
              <div style={S.statDelta}>{s.delta}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...S.card, padding: "12px 16px" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input placeholder={t.searchPlaceholder} value={search} onChange={e => setSearch(e.target.value)} style={{ ...INP, flex: "1 1 200px", width: "auto", minWidth: 0, maxWidth: isMobile ? "100%" : 260 }} />
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ ...INP, width: "auto", minWidth: 130 }}>
            <option value="all">{t.filterAllRoles}</option>
            {ASSIGNABLE_ROLES.map(r => <option key={r} value={r}>{roleLabels[r] || r}</option>)}
          </select>
          <button onClick={fetchUsers} style={{ marginLeft: isMobile ? 0 : "auto", padding: "7px 14px", borderRadius: 7, background: G3, border: `1px solid ${G}`, color: G, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t.refresh}</button>
        </div>
      </div>

      {error && <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 16px", color: "#ef4444", fontSize: 13 }}>⚠ {error}</div>}

      <div style={{ ...S.tableWrap, ...S.tableScroll }}>
        <div style={{ minWidth: isMobile ? 640 : undefined }}>
          <div style={tblHead(USER_COLS, isMobile)}>
            <span>{t.colName}</span><span>{t.colEmail}</span><span>{t.colCurrentRole}</span><span>{t.colJoined}</span><span>{t.colAssignRole}</span><span>{t.colActions}</span>
          </div>
          {loading && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.loading}</div>}
          {!loading && filtered.length === 0 && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.empty}</div>}
          {!loading && filtered.map((u, i) => (
            <TblRow key={u._id} cols={USER_COLS} isLast={i === filtered.length - 1}>
              <TblCell><span style={{ fontWeight: 600, color: "#111827" }}>{u.name}</span></TblCell>
              <TblCell><span style={{ color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</span></TblCell>
              <TblCell fit><Pill style={rolePill(u.role)}>{roleLabels[u.role] || u.role}</Pill></TblCell>
              <TblCell fit><span style={{ color: "#6b7280", fontSize: 11, whiteSpace: "nowrap" }}>{fmt(u.createdAt)}</span></TblCell>
              <TblCell fit>
                <select
                  value={u.role}
                  disabled={saving === u._id}
                  onChange={e => updateRole(u._id, e.target.value)}
                  style={{ ...TBL_SELECT, opacity: saving === u._id ? 0.6 : 1 }}
                >
                  {ASSIGNABLE_ROLES.map(r => <option key={r} value={r}>{roleLabels[r] || r}</option>)}
                </select>
              </TblCell>
              <TblCell fit>
                <button
                  onClick={() => deleteUserHandler(u._id)}
                  disabled={saving === u._id}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 7,
                    background: "rgba(239,68,68,0.08)",
                    border: "1px solid #fca5a5",
                    color: "#ef4444",
                    fontSize: 13,
                    cursor: saving === u._id ? "not-allowed" : "pointer",
                    opacity: saving === u._id ? 0.6 : 1,
                  }}
                >
                  {t.deleteBtn}
                </button>
              </TblCell>
            </TblRow>
          ))}
        </div>
      </div>
    </div>
  );
}

export default UsersPage;
